import { Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { TenantUsageBucket } from '@/types/schema';

import { usageBucketOptions } from '../AdminTenantsPage.constants';

interface PendingDeletionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (nextUsageBucket: TenantUsageBucket) => void;
  isMarkedForDeletion: boolean;
}

export function PendingDeletionConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isMarkedForDeletion,
}: PendingDeletionConfirmationModalProps) {
  const [nextUsageBucket, setNextUsageBucket] = useState<TenantUsageBucket>(
    isMarkedForDeletion
      ? TenantUsageBucket.Unpaid
      : TenantUsageBucket.PendingDeletion
  );

  const handleConfirm = () => {
    onConfirm(
      isMarkedForDeletion ? nextUsageBucket : TenantUsageBucket.PendingDeletion
    );
    onClose();
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={onClose}
      heading={
        isMarkedForDeletion
          ? 'Remove from deletion queue'
          : 'Mark tenant for deletion'
      }
      actions={
        <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
          <Button onClick={onClose} variant="secondary" size="sm" fullWidth>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant={isMarkedForDeletion ? 'primary' : 'destructive-primary'}
            size="sm"
            fullWidth
          >
            {isMarkedForDeletion ? 'Remove deletion tag' : 'Mark for deletion'}
          </Button>
        </Stack>
      }
    >
      <Stack maxWidth={400} spacing={2}>
        <Typography variant="body-s">
          {isMarkedForDeletion ? (
            <>
              Are you sure you want to remove the deletion tag from this tenant?
              This will cancel the scheduled deletion. If you remove and re-add
              the deletion tag, it will be reset to a 30 day window.
            </>
          ) : (
            <>
              Are you sure you want to mark this tenant for deletion in 30 days?
              This action will schedule the tenant to be deleted automatically.
            </>
          )}
        </Typography>
        {isMarkedForDeletion && (
          <Stack spacing={1}>
            <SelectInput
              label="Usage bucket to restore tenant to"
              value={nextUsageBucket}
              onChange={(event) =>
                setNextUsageBucket(event.target.value as TenantUsageBucket)
              }
              options={usageBucketOptions}
            />
          </Stack>
        )}
      </Stack>
    </DialogModal>
  );
}
